'use client';

import { useState, useRef } from 'react';
import Link from 'next/link';
import { Search, Loader2 } from 'lucide-react';
import { blogService, BlogPost, BlogResponse } from '@/services/blogService';
import BlogCard from './BlogCard';

const categories = [
  { name: 'All', slug: '', href: '/blog' },
  { name: 'Marathi Lifestyle', slug: 'marathi-lifestyle', href: '/blog/category/marathi-lifestyle' },
  { name: 'Entertainment', slug: 'entertainment', href: '/blog/category/entertainment' }
];

interface BlogClientProps {
  initialPosts: BlogPost[];
  initialPagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  initialCategory?: string;
}

export default function BlogClient({ 
  initialPosts, 
  initialPagination, 
  initialCategory = 'All' 
}: BlogClientProps) {
  const [selectedCategory] = useState(initialCategory);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [posts, setPosts] = useState<BlogPost[]>(initialPosts);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState(initialPagination);
  const [hasMorePosts, setHasMorePosts] = useState(initialPagination.page < initialPagination.pages);
  const isRequestInProgress = useRef(false);

  // Fetch blog posts from API
  const fetchBlogPosts = async (page: number = 1, category?: string, search?: string, append: boolean = false) => {
    // Prevent duplicate calls
    if (isRequestInProgress.current && !append) {
      return;
    }
    
    isRequestInProgress.current = true;
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      let response: BlogResponse;
      
      if (search) {
        response = await blogService.searchBlogPosts(search, page, 18);
      } else if (category && category !== 'All') {
        response = await blogService.getBlogPostsByCategory(category.toLowerCase().replace(/\s+/g, '-'), page, 12);
      } else {
        response = await blogService.getBlogPosts(page, 12);
      }
      
      if (append) {
        setPosts(prev => [...prev, ...response.data.posts]);
      } else {
        setPosts(response.data.posts);
      }
      
      setPagination(response.data.pagination);
      setHasMorePosts(page < response.data.pagination.pages);
    } catch {
      // Error fetching blog posts
      setError('Failed to load blog posts. Please try again.');
      if (!append) {
        setPosts([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isRequestInProgress.current = false;
    }
  };

  // Note: Category changes are now handled via URL navigation (Link components)
  // No need for useEffect to handle category changes

  // Handle search form submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const categorySlug = selectedCategory === 'All' ? '' : selectedCategory.toLowerCase().replace(/\s+/g, '-');
    fetchBlogPosts(1, categorySlug, searchQuery);
    setCurrentPage(1);
  };

  // Handle search input change (without triggering search)
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Clear search and load all posts
  const clearSearch = () => {
    setSearchQuery('');
    const categorySlug = selectedCategory === 'All' ? '' : selectedCategory.toLowerCase().replace(/\s+/g, '-');
    fetchBlogPosts(1, categorySlug, '');
    setCurrentPage(1);
  };

  // Load more posts function
  const loadMorePosts = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    const categorySlug = selectedCategory === 'All' ? '' : selectedCategory.toLowerCase().replace(/\s+/g, '-');
    fetchBlogPosts(nextPage, categorySlug, searchQuery, true);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl hero-heading font-bold text-gray-900 mb-6">
              Marathi Vadhu Var Suchak Blogs
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Expert insights, success stories, and valuable tips for your matrimonial journey
            </p>
            
            {/* Search Bar */}
            <form onSubmit={handleSearchSubmit} className="max-w-2xl mx-auto">
              <div className="relative flex max-[599px]:flex-col max-[599px]:gap-3">
                <div className="relative flex-1 max-[599px]:w-full">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search articles, tips, and stories..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg"
                  />
                </div>
                <button
                  type="submit"
                  className="ml-3 max-[599px]:ml-0 px-8 py-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 font-medium"
                >
                  Search
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3 max-[599px]:flex-nowrap max-[599px]:overflow-x-auto max-[599px]:justify-start max-[599px]:pb-2">
            {categories.map((category) => (
              <Link
                key={category.slug}
                href={category.href}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 max-[599px]:flex-shrink-0 ${
                  selectedCategory === category.name
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Search Results Header */}
      {searchQuery && (
        <section className="py-8 bg-gray-50 border-b border-gray-200">
          <div className="container mx-auto px-4">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Search Results for &quot;{searchQuery}&quot;
              </h2>
              <p className="text-gray-600">
                Found {pagination.total} article{pagination.total !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
              <p className="text-gray-600">
                {searchQuery ? 'Searching articles...' : 'Loading blog posts...'}
              </p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-medium text-red-600 mb-2">Error loading posts</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button 
                onClick={() => {
                  const categorySlug = selectedCategory === 'All' ? '' : selectedCategory.toLowerCase().replace(/\s+/g, '-');
                  fetchBlogPosts(currentPage, categorySlug, searchQuery);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16">
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                {searchQuery ? 'No articles found for your search' : 'No articles found'}
              </h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? `Try searching for different keywords or browse all articles`
                  : 'Try adjusting your search or category filter'
                }
              </p>
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard key={post.id} post={post} formatDate={formatDate} />
              ))}
            </div>
          )}

          {/* Posts Count and Load More */}
          <div className="mt-12">
            {/* Posts Count */}
            <div className="text-center mb-6">
              <p className="text-gray-600">
                {searchQuery ? (
                  <>
                    Showing {posts.length} of {pagination.total} search results for &quot;{searchQuery}&quot;
                  </>
                ) : (
                  <>
                    Showing {posts.length} of {pagination.total} articles
                  </>
                )}
              </p>
            </div>

            {/* Load More Button */}
            {hasMorePosts && (
              <div className="text-center">
                <button
                  onClick={loadMorePosts}
                  disabled={loadingMore}
                  className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 ${
                    loadingMore
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg'
                  }`}
                >
                  {loadingMore ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Loading More...</span>
                    </div>
                  ) : (
                    searchQuery ? 'Load More Search Results' : 'Load More Articles'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
